// src/pages/Admin/CoursAgenda/utils/coursAgendaUtils.js

import { getInstructorAccentColor } from "../data/coursAgendaUi";

/* -------------------------------------------------------------------------- */
/*                                  Textes                                    */
/* -------------------------------------------------------------------------- */

export const normalizeText = (value = "") =>
  value
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

export const getPersonName = (person) => {
  if (!person) return "";

  return [person.prenom, person.nom]
    .filter(Boolean)
    .join(" ")
    .trim();
};

export const sortPeople = (people = []) =>
  [...people].sort((a, b) => {
    const nameA = `${a.nom || ""} ${a.prenom || ""}`;
    const nameB = `${b.nom || ""} ${b.prenom || ""}`;

    return nameA.localeCompare(nameB, "fr", {
      sensitivity: "base",
    });
  });

/* -------------------------------------------------------------------------- */
/*                                    IDs                                     */
/* -------------------------------------------------------------------------- */

export const parseIds = (value) => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? [value] : [];
  }

  if (typeof value === "string") {
    const trimmed = value.trim();

    if (!trimmed) {
      return [];
    }

    try {
      const parsed = JSON.parse(trimmed);

      if (Array.isArray(parsed)) {
        return parsed
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id));
      }
    } catch {
      // fallback CSV ci-dessous
    }

    return trimmed
      .split(",")
      .map((id) => Number(id.trim()))
      .filter((id) => Number.isFinite(id));
  }

  return [];
};

/* -------------------------------------------------------------------------- */
/*                                   Cours                                    */
/* -------------------------------------------------------------------------- */

export const hydrateCoursAgenda = (
  cours,
  enseignantsList = [],
  assistantsList = []
) => {
  if (!cours) {
    return null;
  }

  let enseignantsIds = parseIds(cours.enseignants_ids);
  let assistantsIds = parseIds(cours.assistants_ids);

  /*
   * Fallback si l'API renvoie déjà des objets enseignants/assistants
   * mais pas les champs *_ids.
   */
  if (
    enseignantsIds.length === 0 &&
    Array.isArray(cours.enseignants)
  ) {
    enseignantsIds = cours.enseignants
      .map((person) => Number(person?.id))
      .filter(Number.isFinite);
  }

  if (
    assistantsIds.length === 0 &&
    Array.isArray(cours.assistants)
  ) {
    assistantsIds = cours.assistants
      .map((person) => Number(person?.id))
      .filter(Number.isFinite);
  }

  const enseignants = enseignantsList.filter((enseignant) =>
    enseignantsIds.includes(Number(enseignant.id))
  );

  const assistants = assistantsList.filter((assistant) =>
    assistantsIds.includes(Number(assistant.id))
  );

  /*
   * Si jamais l'API renvoie déjà les objets et qu'ils ne sont pas
   * présents dans les listes générales, on les conserve.
   */
  const finalEnseignants =
    enseignants.length > 0
      ? enseignants
      : Array.isArray(cours.enseignants)
      ? cours.enseignants
      : [];

  const finalAssistants =
    assistants.length > 0
      ? assistants
      : Array.isArray(cours.assistants)
      ? cours.assistants
      : [];

  const firstInstructorId =
    enseignantsIds[0] || finalEnseignants?.[0]?.id;

  /*
   * IMPORTANT :
   * ...cours est placé AVANT nos propriétés calculées.
   * Il ne peut donc plus écraser enseignants / assistants.
   */
  return {
    ...cours,

    enseignants_ids: enseignantsIds,
    assistants_ids: assistantsIds,

    enseignants: finalEnseignants,
    assistants: finalAssistants,

    accentColor: getInstructorAccentColor(firstInstructorId),
  };
};

/* -------------------------------------------------------------------------- */
/*                                    Date                                    */
/* -------------------------------------------------------------------------- */

export const toDateInputValue = (value) => {
  if (!value) return "";

  return value.toString().slice(0, 10);
};

export const toTimeInputValue = (value) => {
  if (!value) return "";

  return value.toString().slice(0, 5);
};

export const ymdLocal = (date) => {
  const year = date.getFullYear();

  const month = String(date.getMonth() + 1).padStart(2, "0");

  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const parseLocalDate = (value) => {
  if (!value) return null;

  const ymd = value.toString().slice(0, 10);

  return new Date(`${ymd}T12:00:00`);
};

export const addDays = (date, amount) => {
  const result = new Date(date);

  result.setDate(result.getDate() + amount);

  return result;
};

export const addDaysToYmd = (dateValue, amount) => {
  const date = parseLocalDate(dateValue);

  if (!date) return "";

  return ymdLocal(addDays(date, amount));
};

export const startOfISOWeek = (date = new Date()) => {
  const result = new Date(date);

  result.setHours(12, 0, 0, 0);

  const weekday = (result.getDay() + 6) % 7;

  result.setDate(result.getDate() - weekday);

  return result;
};

export const formatCourseDate = (value) => {
  const date = parseLocalDate(value);

  if (!date) return "Date non renseignée";

  return date.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

export const formatShortDay = (date) =>
  date.toLocaleDateString("fr-FR", {
    weekday: "long",
  });

export const formatShortDate = (date) =>
  date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
  });

export const formatWeekLabel = (weekStart) => {
  const weekEnd = addDays(weekStart, 6);

  const startLabel = weekStart.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
  });

  const endLabel = weekEnd.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return `${startLabel} au ${endLabel}`;
};

export const isSameLocalDay = (dateA, dateB) =>
  ymdLocal(dateA) === ymdLocal(dateB);